/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Card,
  CardContent,
  CardActions,
  Skeleton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  InputAdornment,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Google as GoogleIcon,
  Sync as SyncIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudDownload as CloudDownloadIcon,
  Close as CloseIcon,
  Folder as FolderIcon,
  FilterList as FilterListIcon,
  Upload as UploadIcon,
  FolderShared as FolderSharedIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
// Date picker imports removed - using basic TextField for dates
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const DOCUMENT_TYPES = [
  { value: 'policy', label: 'Policy Document' },
  { value: 'template', label: 'Template' },
  { value: 'guide', label: 'Guide' },
  { value: 'framework', label: 'Framework' },
  { value: 'methodology', label: 'Methodology' },
  { value: 'standard', label: 'Standard' },
  { value: 'general', label: 'General' },
  { value: 'other', label: 'Other' }
];

// eslint-disable-next-line max-lines-per-function
export default function IPCatalog() {
  const { isPlatformAdmin } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sharedDriveDialogOpen, setSharedDriveDialogOpen] = useState(false); // Deprecated - using Shared Drives tab instead
  const [editMode, setEditMode] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuDocument, setMenuDocument] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [initializingFolders, setInitializingFolders] = useState(false);
  const [initializingCollections, setInitializingCollections] = useState(false);
  const [adminMenuExpanded, setAdminMenuExpanded] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, document: null, action: null });

  // Shared Drive state
  const [sharedDrives, _setSharedDrives] = useState([]);
  const [selectedDrive, setSelectedDrive] = useState(null);
  const [loadingDrives, _setLoadingDrives] = useState(false);
  const [importingDrive, setImportingDrive] = useState(false);
  const [importDocumentType, setImportDocumentType] = useState('general');

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    documentType: '',
    createdBy: '',
    modifiedBy: '',
    dateFrom: null,
    dateTo: null
  });

  // Form fields
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    documentType: 'policy',
    googleDriveFileId: ''
  });

  useEffect(() => {
    fetchDocuments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, searchQuery, filters]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.listFIPDDocuments();
      setDocuments(response.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      showSnackbar('Failed to fetch documents', 'error');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...documents];

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.name?.toLowerCase().includes(query) ||
        doc.description?.toLowerCase().includes(query) ||
        doc.exportedContent?.toLowerCase().includes(query) ||
        doc.createdBy?.toLowerCase().includes(query) ||
        doc.lastModifiedBy?.toLowerCase().includes(query)
      );
    }

    // Apply type filter
    if (filters.documentType) {
      filtered = filtered.filter(doc => doc.documentType === filters.documentType);
    }

    // Apply created by filter
    if (filters.createdBy) {
      filtered = filtered.filter(doc =>
        doc.createdBy?.toLowerCase().includes(filters.createdBy.toLowerCase())
      );
    }

    // Apply modified by filter
    if (filters.modifiedBy) {
      filtered = filtered.filter(doc =>
        doc.lastModifiedBy?.toLowerCase().includes(filters.modifiedBy.toLowerCase())
      );
    }

    // Apply date filters
    if (filters.dateFrom) {
      filtered = filtered.filter(doc =>
        new Date(doc.updatedAt || doc.createdAt) >= filters.dateFrom
      );
    }

    if (filters.dateTo) {
      filtered = filtered.filter(doc =>
        new Date(doc.updatedAt || doc.createdAt) <= filters.dateTo
      );
    }

    setFilteredDocuments(filtered);
  };

  const handleOpenDialog = (document = null) => {
    if (document) {
      setEditMode(true);
      setSelectedDocument(document);
      setFormData({
        name: document.name,
        description: document.description || '',
        documentType: document.documentType,
        googleDriveFileId: document.googleDriveFileId || ''
      });
    } else {
      setEditMode(false);
      setSelectedDocument(null);
      setFormData({
        name: '',
        description: '',
        documentType: 'policy',
        googleDriveFileId: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelectedDocument(null);
    setFormData({
      name: '',
      description: '',
      documentType: 'policy',
      googleDriveFileId: ''
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.documentType) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    try {
      const payload = {
        ...formData,
        documentId: editMode ? selectedDocument.id : null
      };

      await api.manageFIPDDocument(payload);

      showSnackbar(
        editMode ? 'Document updated successfully' : 'Document created successfully',
        'success'
      );
      handleCloseDialog();
      fetchDocuments();
    } catch (error) {
      console.error('Error saving document:', error);
      showSnackbar('Failed to save document', 'error');
    }
  };

  const handleExportContent = async (documentId) => {
    try {
      setExportingId(documentId);
      await api.exportFIPDContent({ documentId });
      showSnackbar('Document content exported successfully', 'success');
      fetchDocuments(); // Refresh to show updated export timestamp
    } catch (error) {
      console.error('Error exporting content:', error);
      showSnackbar('Failed to export document content', 'error');
    } finally {
      setExportingId(null);
    }
  };

  const handleDelete = () => {
    if (!menuDocument) return;
    setConfirmDialog({
      open: true,
      document: menuDocument,
      action: 'archive'
    });
    handleMenuClose();
  };

  const handleRemoveDocument = (document) => {
    setConfirmDialog({
      open: true,
      document: document,
      action: 'remove'
    });
  };

  const handleConfirmAction = async () => {
    const { document, action } = confirmDialog;

    try {
      await api.deleteFIPDDocument({ documentId: document.id });
      showSnackbar(
        action === 'archive' ? 'Document archived successfully' : 'Document removed successfully',
        'success'
      );
      fetchDocuments();
    } catch (error) {
      console.error(`Error ${action === 'archive' ? 'archiving' : 'removing'} document:`, error);
      showSnackbar(
        action === 'archive' ? 'Failed to archive document' : 'Failed to remove document',
        'error'
      );
    }

    setConfirmDialog({ open: false, document: null, action: null });
  };

  const handleMenuClick = (event, document) => {
    setAnchorEl(event.currentTarget);
    setMenuDocument(document);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuDocument(null);
  };

  const handleOpenInDrive = (driveFileId) => {
    if (driveFileId) {
      // Extract the file ID if it's a full URL
      let fileId = driveFileId;
      if (driveFileId.includes('google.com')) {
        const matches = driveFileId.match(/\/d\/([a-zA-Z0-9-_]+)|[?&]id=([a-zA-Z0-9-_]+)/);
        if (matches) {
          fileId = matches[1] || matches[2];
        }
      }
      window.open(`https://docs.google.com/document/d/${fileId}/edit`, '_blank');
    } else {
      showSnackbar('No Google Drive file linked to this document', 'warning');
    }
  };

  const handleInitializeFolders = async (forceRefresh = false) => {
    try {
      setInitializingFolders(true);
      const response = await api.initializePlatformFolders(forceRefresh);
      showSnackbar(
        forceRefresh
          ? 'Platform folders recreated successfully'
          : 'Platform folders initialized successfully',
        'success'
      );
      console.log('Initialize folders response:', response);
    } catch (error) {
      console.error('Error initializing platform folders:', error);
      showSnackbar('Failed to initialize platform folders', 'error');
    } finally {
      setInitializingFolders(false);
    }
  };

  const handleInitializeCollections = async () => {
    try {
      setInitializingCollections(true);
      await api.initializeDMCollections();
      showSnackbar('Document Management collections initialized successfully', 'success');
    } catch (error) {
      console.error('Error initializing collections:', error);
      showSnackbar('Failed to initialize collections', 'error');
    } finally {
      setInitializingCollections(false);
    }
  };


  const handleCloseSharedDriveDialog = () => {
    setSharedDriveDialogOpen(false);
    setSelectedDrive(null);
    setImportDocumentType('general');
  };

  const handleImportSharedDrive = async () => {
    if (!selectedDrive) {
      showSnackbar('Please select a shared drive', 'error');
      return;
    }

    try {
      setImportingDrive(true);
      const response = await api.importSharedDriveDocuments({
        sharedDriveId: selectedDrive.id,
        documentType: importDocumentType,
        includeSubfolders: true
      });

      showSnackbar(
        `Successfully imported ${response.summary.imported} documents from ${selectedDrive.name}`,
        'success'
      );

      if (response.summary.errors > 0) {
        console.warn('Import errors:', response.errors);
        showSnackbar(
          `${response.summary.errors} documents failed to import. Check console for details.`,
          'warning'
        );
      }

      handleCloseSharedDriveDialog();
      fetchDocuments(); // Refresh the document list
    } catch (error) {
      console.error('Error importing shared drive:', error);
      showSnackbar('Failed to import documents from shared drive', 'error');
    } finally {
      setImportingDrive(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const clearFilters = () => {
    setFilters({
      documentType: '',
      createdBy: '',
      modifiedBy: '',
      dateFrom: null,
      dateTo: null
    });
    setSearchQuery('');
  };

  // eslint-disable-next-line max-lines-per-function
  const renderDocumentCard = (document) => (
    <Card key={document.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="h3" gutterBottom>
            {document.name}
          </Typography>
          {isPlatformAdmin && (
            <IconButton size="small" onClick={(e) => handleMenuClick(e, document)}>
              <MoreVertIcon />
            </IconButton>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            label={DOCUMENT_TYPES.find(t => t.value === document.documentType)?.label || document.documentType}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`v${document.version || '1.0.0'}`}
            size="small"
            variant="outlined"
          />
          {/* Extraction Status Indicator */}
          {document.exportedContent && document.exportedContent.length > 0 ? (
            <Tooltip title={`Content extracted and cached for AI access. Last extracted: ${document.lastExportedAt ? new Date(document.lastExportedAt).toLocaleDateString() : 'Unknown'}`}>
              <Chip
                icon={<CheckCircleIcon />}
                label="Extracted"
                size="small"
                color="success"
                variant="outlined"
              />
            </Tooltip>
          ) : (
            <Tooltip title="Content not yet extracted. Click 'Export content for AI' to enable fast AI access to this document.">
              <Chip
                icon={<WarningIcon />}
                label="Not Extracted"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ cursor: 'pointer' }}
                onClick={() => isPlatformAdmin && handleExportContent(document.id)}
              />
            </Tooltip>
          )}
        </Box>

        {document.description && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {document.description}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          Last modified: {new Date(document.updatedAt || document.createdAt).toLocaleDateString()}
          {document.lastModifiedBy && ` by ${document.lastModifiedBy}`}
        </Typography>

        {document.lastExportedAt && (
          <Typography variant="caption" color="text.secondary" display="block">
            Exported: {new Date(document.lastExportedAt).toLocaleDateString()}
          </Typography>
        )}

        {document.wordCount > 0 && (
          <Typography variant="caption" color="text.secondary" display="block">
            {document.wordCount} words
          </Typography>
        )}
      </CardContent>

      <CardActions>
        {document.googleDriveFileId && (
          <Tooltip title="View in Google Docs">
            <IconButton size="small" onClick={() => handleOpenInDrive(document.googleDriveFileId)}>
              <GoogleIcon />
            </IconButton>
          </Tooltip>
        )}
        {isPlatformAdmin && (
          <Tooltip title="Export content for AI">
            <IconButton
              size="small"
              onClick={() => handleExportContent(document.id)}
              disabled={exportingId === document.id}
            >
              {exportingId === document.id ? (
                <CircularProgress size={20} />
              ) : (
                <SyncIcon />
              )}
            </IconButton>
          </Tooltip>
        )}
        {isPlatformAdmin && (
          <Tooltip title="Remove from library">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleRemoveDocument(document)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">IP Catalog</Typography>
            <Tooltip title="Documents are automatically processed when added. Green checkmark means content is cached for fast AI access.">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {isPlatformAdmin && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => alert('Upload from computer functionality coming soon')}
              >
                Upload from Computer
              </Button>
              <Button
                variant="outlined"
                startIcon={<FolderSharedIcon />}
                onClick={() => {
                  // Navigate to Shared Drives tab
                  const event = new CustomEvent('changeDocumentTab', { detail: { tabIndex: 3 } });
                  window.dispatchEvent(event);
                }}
              >
                Browse Shared Drives
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add Document
              </Button>
            </Box>
          )}
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search documents by name, description, content, or author..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>
          {(searchQuery || filters.documentType || filters.createdBy || filters.modifiedBy || filters.dateFrom || filters.dateTo) && (
            <Button
              variant="text"
              size="small"
              onClick={clearFilters}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        <Collapse in={showFilters}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={filters.documentType}
                    onChange={(e) => setFilters({ ...filters, documentType: e.target.value })}
                    label="Document Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    {DOCUMENT_TYPES.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Created By"
                  value={filters.createdBy}
                  onChange={(e) => setFilters({ ...filters, createdBy: e.target.value })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Modified By"
                  value={filters.modifiedBy}
                  onChange={(e) => setFilters({ ...filters, modifiedBy: e.target.value })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Date From"
                  type="date"
                  value={filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : ''}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value ? new Date(e.target.value) : null })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        {/* Admin Menu - Collapsible */}
        {isPlatformAdmin && (
          <Box sx={{ mb: 2 }}>
            <Button
              size="small"
              onClick={() => setAdminMenuExpanded(!adminMenuExpanded)}
              endIcon={adminMenuExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              Admin Tools
            </Button>
            <Collapse in={adminMenuExpanded}>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Tooltip title="Initialize FGOS Platform folder structure">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={initializingFolders ? <CircularProgress size={20} /> : <SyncIcon />}
                    onClick={() => handleInitializeFolders(false)}
                    disabled={initializingFolders}
                  >
                    Init Folders
                  </Button>
                </Tooltip>
                <Tooltip title="Force recreate folders (clears cached configuration)">
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={initializingFolders ? <CircularProgress size={20} /> : <SyncIcon />}
                    onClick={() => handleInitializeFolders(true)}
                    disabled={initializingFolders}
                  >
                    Force Init
                  </Button>
                </Tooltip>
                <Tooltip title="Initialize Document Management collections in Firestore">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={initializingCollections ? <CircularProgress size={20} /> : <SyncIcon />}
                    onClick={handleInitializeCollections}
                    disabled={initializingCollections}
                  >
                    Init Collections
                  </Button>
                </Tooltip>
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3].map(i => (
            <Grid
              key={i}
              size={{
                xs: 12,
                md: 6,
                lg: 4
              }}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          ))}
        </Grid>
      ) : filteredDocuments.length === 0 ? (
        <Alert severity="info">
          {searchQuery || Object.values(filters).some(v => v)
            ? 'No documents match your search criteria.'
            : 'No documents found. ' + (isPlatformAdmin ? 'Add your first document to get started.' : '')
          }
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {filteredDocuments.map(document => (
            <Grid
              key={document.id}
              size={{
                xs: 12,
                md: 6,
                lg: 4
              }}>
              {renderDocumentCard(document)}
            </Grid>
          ))}
        </Grid>
      )}
      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Edit Document' : 'Add Document'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Document Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />

          <FormControl fullWidth margin="dense" variant="outlined">
            <InputLabel>Document Type</InputLabel>
            <Select
              value={formData.documentType}
              onChange={(e) => setFormData({ ...formData, documentType: e.target.value })}
              label="Document Type"
            >
              {DOCUMENT_TYPES.map(type => (
                <SelectMenuItem key={type.value} value={type.value}>
                  {type.label}
                </SelectMenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label="Google Drive File ID or URL"
            fullWidth
            variant="outlined"
            value={formData.googleDriveFileId}
            onChange={(e) => setFormData({ ...formData, googleDriveFileId: e.target.value })}
            helperText="Optional: Link to existing Google Doc (paste full URL or just the file ID)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Shared Drive Import Dialog */}
      <Dialog
        open={sharedDriveDialogOpen}
        onClose={handleCloseSharedDriveDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import Documents from Shared Drive</DialogTitle>
        <DialogContent>
          {loadingDrives ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Select a shared drive to import all Google Docs as Intelligence Hub documents.
              </Typography>

              <List sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                {sharedDrives.length === 0 ? (
                  <Alert severity="info">
                    No shared drives found. Make sure you have access to at least one shared drive.
                  </Alert>
                ) : (
                  sharedDrives.map(drive => (
                    <ListItem key={drive.id} disablePadding>
                      <ListItemButton
                        selected={selectedDrive?.id === drive.id}
                        onClick={() => setSelectedDrive(drive)}
                      >
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <FolderIcon />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={drive.name}
                          secondary={`Created: ${new Date(drive.createdTime).toLocaleDateString()}`}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))
                )}
              </List>

              <Divider sx={{ my: 2 }} />

              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel>Document Type for Imported Documents</InputLabel>
                <Select
                  value={importDocumentType}
                  onChange={(e) => setImportDocumentType(e.target.value)}
                  label="Document Type for Imported Documents"
                >
                  {DOCUMENT_TYPES.map(type => (
                    <SelectMenuItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectMenuItem>
                  ))}
                </Select>
              </FormControl>

              <Alert severity="info" sx={{ mt: 2 }}>
                All Google Docs in the selected drive will be imported to the IP Catalog. The first 3 sentences
                of each document will be used as the description.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSharedDriveDialog} disabled={importingDrive}>
            Cancel
          </Button>
          <Button
            onClick={handleImportSharedDrive}
            variant="contained"
            disabled={!selectedDrive || importingDrive}
            startIcon={importingDrive ? <CircularProgress size={20} /> : <CloudDownloadIcon />}
          >
            {importingDrive ? 'Importing...' : 'Connect & Import'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleOpenDialog(menuDocument);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Archive</ListItemText>
        </MenuItem>
      </Menu>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, document: null, action: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.action === 'archive' ? 'Archive Document' : 'Remove Document'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog.action === 'archive' ? 'archive' : 'remove'} "{confirmDialog.document?.name}"
            {confirmDialog.action === 'remove' ? ' from the IP Catalog' : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, document: null, action: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color="error"
            variant="contained"
          >
            {confirmDialog.action === 'archive' ? 'Archive' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}